import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { Badge } from "@/components/ui/badge";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";

type Ticket = {
  id: string;
  status: "PENDING" | "IN_REVIEW" | "FIXED" | "DELAYED";
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  message: string;
  createdAt: string;
  updatedAt: string;
  professionalName: string | null;
  professionalSlug: string | null;
};

function tone(status: Ticket["status"]): "warning" | "neutral" | "success" {
  if (status === "PENDING") return "warning";
  if (status === "IN_REVIEW" || status === "DELAYED") return "neutral";
  return "success";
}

function label(status: Ticket["status"]): string {
  if (status === "PENDING") return "Pendiente";
  if (status === "IN_REVIEW") return "En revisión";
  if (status === "DELAYED") return "Demorado";
  return "Arreglado";
}

export function AdminTickets() {
  const { refresh } = useAuth();
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/admin/tickets", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) return;
      setTickets((await r.json()) as Ticket[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  async function updateStatus(id: string, status: Ticket["status"]) {
    setSavingId(id);
    const r = await fetchWithSessionSync(
      `/api/admin/tickets/${id}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      },
      refresh,
    );
    setSavingId(null);
    if (!r.ok || !tickets) return;
    setTickets((prev) => (prev ? prev.map((t) => (t.id === id ? { ...t, status } : t)) : prev));
  }

  if (!tickets) {
    return (
      <AdminPageShell title="Tickets">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell title="Tickets" description="Reportes de ayuda enviados por clientes.">
      {tickets.length === 0 ? (
        <EmptyState
          title="Sin tickets"
          description="Cuando un cliente reporte un problema desde Ayuda, aparecerá acá."
          icon="🎫"
        />
      ) : (
        <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
          <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
            <thead>
              <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", whiteSpace: "nowrap" })}>Fecha</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Estado</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Solicitante</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Contacto</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Agenda</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", minW: "280px" })}>Problema</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                  <td className={css({ py: "3", px: "4", color: "fg.muted", whiteSpace: "nowrap", fontSize: "xs" })}>
                    {format(new Date(t.createdAt), "d MMM yyyy HH:mm", { locale: es })}
                  </td>
                  <td className={css({ py: "3", px: "4" })}>
                    <div className={css({ display: "flex", gap: "2", alignItems: "center" })}>
                      <Badge tone={tone(t.status)}>{label(t.status)}</Badge>
                      <select
                        value={t.status}
                        disabled={savingId === t.id}
                        onChange={(e) => void updateStatus(t.id, e.target.value as Ticket["status"])}
                        className={css({ h: "8", px: "2", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "xs" })}
                      >
                        <option value="PENDING">Pendiente</option>
                        <option value="IN_REVIEW">En revisión</option>
                        <option value="FIXED">Arreglado</option>
                        <option value="DELAYED">Demorado</option>
                      </select>
                    </div>
                  </td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs" })}>{t.clientName}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs", whiteSpace: "nowrap" })}>
                    <div>{t.clientEmail}</div>
                    <div>{t.clientPhone ?? "—"}</div>
                  </td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs" })}>
                    {t.professionalName ? `${t.professionalName}${t.professionalSlug ? ` (${t.professionalSlug})` : ""}` : "—"}
                  </td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs", color: "fg.muted", lineHeight: "1.5" })}>{t.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminPageShell>
  );
}

